import React from 'react';
import { Room } from 'livekit-client';
import { createPortal } from 'react-dom';
import { useGlobalLiveKitRoomState } from '../chat/[avatarId]/hooks/useGlobalChatState';

function useAudioUnlock(room: Room | null) {
  const [canPlay, setCanPlay] = React.useState<boolean>(() => !!room?.canPlaybackAudio);
  const [attempting, setAttempting] = React.useState(false);

  React.useEffect(() => {
    if (!room) return;
    
    const handle = () => setCanPlay(room.canPlaybackAudio);
    
    // Check initial state
    handle();
    
    // Poll for changes since RoomEvent.CanPlayAudioChanged doesn't exist
    const interval = setInterval(handle, 100);
    
    return () => {
      clearInterval(interval);
    };
  }, [room]);

  const tryUnlock = React.useCallback(async () => {
    if (!room || attempting || canPlay) return;
    setAttempting(true);
    try {
      // 1) The intended path
      await room.startAudio();

      // 2) If still not unlocked, iOS fallback - play 1-frame silent audio element
      if (!room.canPlaybackAudio) {
        const el = document.createElement('audio');
        el.setAttribute('playsinline', '');
        el.src = 'data:audio/mp3;base64,//uQZAAAAAAAAAAAAAAAAAAAA...'; // very short silent mp3
        // Do not add to DOM; just playing is enough
        try {
          await el.play();
          el.pause();
          el.src = '';
        } catch {}
        // Re-attempt now that the device is unlocked
        if (!room.canPlaybackAudio) await room.startAudio();
      }
    } catch (e) {
      console.error('Audio unlock failed', e);
    } finally {
      setAttempting(false);
    }
  }, [room, attempting, canPlay]);

  return { canPlay, tryUnlock };
}

export default function AudioUnlocker() {
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  const room = globalLKRoomState.room;
  const { canPlay, tryUnlock } = useAudioUnlock(room);

  // Mount as early as possible when a room exists
  React.useEffect(() => {
    if (!room) return;

    // Global capture as a backstop (in case overlay is not on top / timing issues)
    const handler = () => tryUnlock();
    // pointerdown is the most reliable unified gesture
    document.addEventListener('pointerdown', handler, { once: false, passive: true });
    document.addEventListener('click', handler, { once: false, passive: true });
    return () => {
      document.removeEventListener('pointerdown', handler as any);
      document.removeEventListener('click', handler as any);
    };
  }, [room, tryUnlock]);

  if (!room || canPlay) return null;

  // Fullscreen overlay as primary target (ensure truly on top)
  return createPortal(
    <div
      role="button"
      tabIndex={0}
      aria-label="Enable audio"
      onPointerDown={tryUnlock}
      onClick={tryUnlock}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') tryUnlock();
      }}
      className="fixed inset-0"
      style={{
        zIndex: 2147483647, // above everything
        pointerEvents: 'auto',
        // transparent hit area (you could add a tiny toast if you want)
        background: 'transparent',
      }}
    />,
    document.body
  );
} 